/**
 * Brand Bible v2 — file drop + reader agent API client (contract Appendix B, B.2).
 * All routes carry ?tenant_id= (the RLS bridge, same pattern as brandBibleV2Service).
 * Shapes are pinned — callers render statuses/counts verbatim, never invent new
 * server-vocabulary words (see FileDropZone.tsx / BbFileDropCard.tsx).
 */
import { apiClient } from '@/services/apiClient';

const BASE = '/brand-bible/v2';

function params(tenantId: string) {
  return { params: { tenant_id: tenantId } };
}

export type BbFileUploadStatus = 'stored' | 'duplicate' | 'rejected' | 'restored';

export interface BbFileUploadResult {
  file_id: string | null;
  name: string;
  status: BbFileUploadStatus;
  reason?: string;
}

export interface BbFilesUploadResponse {
  files: BbFileUploadResult[];
  stored: number;
  duplicate: number;
  rejected: number;
  /** BB_FILE_READER_ENABLED, echoed on upload so a fresh upload knows whether to
   * auto-fire the read (contract B.2). Optional on the type only so an older
   * backend that omits it degrades to "unknown" rather than throwing. */
  reader_enabled?: boolean;
}

/** bb_files.status — the DB/backend vocabulary (contract B.1), distinct from the
 * FE's DropFileState presentation states. */
export type BbFileStatus = 'stored' | 'parsing' | 'parsed' | 'failed';

export interface BbFileRow {
  id: string;
  filename: string;
  mime: string;
  bytes_size: number;
  sha256: string;
  status: BbFileStatus;
  parse_error: string | null;
  reader_model: string | null;
  created_at: string;
  deleted_at?: string | null;
}

export interface BbFilesListResponse {
  files: BbFileRow[];
  /** BB_FILE_READER_ENABLED (contract B.2). This flag is the ONLY way the FE
   * learns whether the reader will run — without it the FE has to fire a doomed
   * POST /read and catch the 503 to discover the flag state, one wasted
   * round-trip per file on every flag-off environment. Surface it, don't guess.
   * Optional on the type so an older backend degrades gracefully. */
  reader_enabled?: boolean;
}

export type BbReadStatus = 'parsed' | 'failed';

export interface BbFileReadResponse {
  file_id: string;
  status: BbReadStatus;
  entries_drafted: number;
  skipped_existing: number;
  dropped_invalid: number;
  sections: Record<string, number>;
  reason?: string;
}

export interface BbExtraction {
  entry_id: string;
  table_name: string;
  file_id: string;
  filename: string;
  quote: string;
  location: string | null;
}

export interface BbExtractionsResponse {
  extractions: BbExtraction[];
}

export const bbFilesService = {
  /** Multipart upload — up to 10 files / 15 MiB each (contract B.2). One bad file
   * never fails the batch; per-file status comes back truthful. */
  async uploadFiles(tenantId: string, files: File[]): Promise<BbFilesUploadResponse> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const { data } = await apiClient.post<BbFilesUploadResponse>(`${BASE}/files`, form, {
      params: { tenant_id: tenantId, auto_read: false },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000, // up to 10 files, 15 MiB each — default 30s is tight
    });
    return data;
  },

  async listFiles(tenantId: string): Promise<BbFilesListResponse> {
    const { data } = await apiClient.get<BbFilesListResponse>(`${BASE}/files`, params(tenantId));
    return data;
  },

  /** Provenance for the NeedsAttentionQueue's file chips (B.4). */
  async listExtractions(tenantId: string): Promise<BbExtractionsResponse> {
    const { data } = await apiClient.get<BbExtractionsResponse>(`${BASE}/extractions`, params(tenantId));
    return data;
  },

  /**
   * Fires the LLM read. Editor+ role; 503 when BB_FILE_READER_ENABLED is off
   * (caller renders that as "reader not enabled", not a generic error — the
   * flag has no other client-visible signal, contract B.3).
   */
  async readFile(tenantId: string, fileId: string): Promise<BbFileReadResponse> {
    const { data } = await apiClient.post<BbFileReadResponse>(
      `${BASE}/files/${fileId}/read`,
      null,
      { ...params(tenantId), timeout: 120_000 },
    );
    return data;
  },

  /**
   * Streams a stored original back for preview (source-doc library, B-FE-2).
   * Returns the raw Blob — the caller owns object-URL creation AND revocation;
   * this service holds no browser state.
   */
  async fetchFileContent(tenantId: string, fileId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`${BASE}/files/${fileId}/content`, {
      ...params(tenantId),
      responseType: 'blob',
    });
    return data;
  },

  /** Soft-deletes a stored file (the row keeps its sha256 — see the dedup-after-
   * delete caveat the backend audit raised). */
  async deleteFile(tenantId: string, fileId: string): Promise<void> {
    await apiClient.delete(`${BASE}/files/${fileId}`, params(tenantId));
  },
};
